import { Fragment, useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { fetchPhoenixContact, type PhoenixContact } from "../api/phoenixContact";
import { Skeleton, useDocumentTitle } from "@/design-system/components/ui";
import { usePortalAuth } from "../PortalAuthContext";
import type { PartnerBrandSkin } from "../api/partnerBrand";
import { POWERED_BY_PHOENIX } from "../pages/prequalify/prequalContent";
import { useFunnelCopy } from "../pages/prequalify/usePartnerBrand";
import { hasText } from "../pages/prequalify/blankCopy";

// External Intake & Collection v1.0 PR 4 §2.1 / §4 — the persistent
// portal chrome. Mobile-first, gold-on-onyx Phoenix branding, a clear
// "from Phoenix Capital" identity, a help / contact affordance, and a
// short reassurance about document encryption. Renders the same header
// on the redeem flow and the package surface so trust cues stay
// consistent (anti-phishing baseline, spec §10).
//
// Prequalification Experience Rollout — `publicFunnel` chrome variant
// for the anonymous prequalification pages ONLY: the header swaps the
// "Sign in" link for the questions phone line, the trust band carries
// the funnel reassurance copy, and the footer identifies the LLC + Loan
// & Note Administrator role with contact details (no "My dashboard" —
// an anonymous prospect has none). Default off; every other PortalShell
// consumer renders byte-identically.

interface PortalShellProps {
  children: ReactNode;
  /** Optional right-side title shown next to the wordmark — e.g. "My
   *  documents" or "Verify it's you". */
  pageTitle?: string;
  /** If set, the shell renders a subtle "from {brand}" attribution band
   *  beneath the header. Defaults to "Phoenix Capital". */
  brand?: string;
  /** Wide content container (~1152px) for two-column surfaces like the
   *  prequalification funnel. Default off — every other pre-auth page
   *  keeps the reading-width shell. */
  wide?: boolean;
  /** Public-funnel chrome (anonymous prequalification pages): phone line
   *  in the header, funnel trust band, LLC footer. Default off. */
  publicFunnel?: boolean;
  /** White-label: when set (with `publicFunnel`), the chrome is themed to the
   *  partner — the wordmark, header/footer tones, and the support/contact
   *  block use the partner skin. Null/undefined → native Phoenix chrome
   *  (byte-identical). */
  partner?: PartnerBrandSkin | null;
  /**
   * Trim the chrome to what an OPERATOR needs (assisted completion).
   *
   * Drops the trust band and the "Secure Portal" caption and tightens the
   * header. Those are reassurance written for a cold anonymous visitor — "no
   * credit pull", "free", "no account needed" — and the person reading this
   * screen was walked to it by someone they already know. The band is also
   * hardcoded "Phoenix Capital" even under a partner skin, so on a branded
   * assisted application it names the wrong company.
   *
   * <b>The header bar itself SURVIVES, and that is the point.</b> It carries the
   * mark, the primary colour and the partner's phone number — the entire brand
   * payload of the page. Dropping it to reclaim space would delete the only
   * thing identifying whose application the client is filling in.
   *
   * Default off: every other consumer renders byte-identically.
   */
  compact?: boolean;
  /**
   * A partner brand is still resolving for this route.
   *
   * The chrome HOLDS rather than guessing: header and footer render a shimmer
   * at their final heights and paint once, when the answer arrives. Guessing
   * meant painting Phoenix — the dark bar, the wordmark, the "Secure Portal"
   * caption and the Phoenix phone number — on a route that was about to resolve
   * to somebody else, on every load and every refresh.
   *
   * <b>The pending header is NEUTRAL, not the Phoenix bar.</b> Falling back to
   * `bg-onyx` here would defeat the point: the dark bar is itself a Phoenix cue,
   * so it is replaced by a plain grey while the brand is unknown.
   *
   * <b>`<main>` is unaffected.</b> The page's own content renders throughout —
   * this holds the CHROME, never the page, and there is no moment at which the
   * applicant is looking at a blank screen.
   *
   * <b>The locked "Powered by Phoenix" strip is unaffected too.</b> It is an
   * attribution that renders on every funnel view, native and partner alike; it
   * is not brand payload and there is nothing about it to wait for.
   *
   * Default off: every consumer outside the prequalification flow renders
   * byte-identically, and the NATIVE funnel never sets it — a Phoenix
   * application resolves synchronously and must not acquire a skeleton.
   */
  brandPending?: boolean;
}

const SUPPORT_EMAIL = "support@phoenixcapital.solutions";

/**
 * The HEADER's "Questions?" phone — still a literal.
 *
 * ⚠️ Deliberately left hardcoded, and reported rather than fixed: the header line
 * is a different element from the footer's contact line, which is what this change
 * was scoped to. The consequence is real and worth knowing — the funnel footer now
 * shows Brand Identity's phone while the header still shows this constant, so the
 * two can disagree the moment an admin edits the field. Pointing it at the same
 * source is a small follow-up; doing it here would have been an unrequested visible
 * change to the header.
 */
const SUPPORT_PHONE = "(572) 218-3343";

/** tel: href from a display phone (strip everything but digits + a leading +). */
function telHref(phone: string): string {
  const cleaned = phone.replace(/[^\d+]/g, "");
  return `tel:${cleaned}`;
}

/** Prefix a bare host with https:// for an anchor href. */
function websiteHref(site: string): string {
  return /^https?:\/\//i.test(site) ? site : `https://${site}`;
}

/**
 * A website URL as a human reads it: no scheme, no trailing slash.
 *
 * `www` is KEPT — it is part of how a business writes its own address — and any
 * path or query survives verbatim, because a partner whose funnel points at a
 * specific landing page needs that page named. Only ONE trailing slash is stripped,
 * and only from the very end, so a URL whose path is meaningful is not quietly
 * truncated. The href keeps the full value (see {@link websiteHref}).
 */
function websiteLabel(site: string): string {
  return site.replace(/^https?:\/\//i, "").replace(/\/$/, "");
}

/** One contact element. `kind` picks the link scheme and the display transform. */
type ContactItem = { kind: "email" | "phone" | "website"; value?: string | null };

/**
 * The funnel footer's contact line — the caller's items, in the caller's order,
 * separated by " · ".
 *
 * 🔴 <b>An empty value renders NOTHING — no bare link, and no orphaned separator.</b>
 * That is the OPPOSITE of how the funnel's copy blocks behave, where an empty
 * override falls back to a shipped default. The asymmetry is deliberate: a missing
 * phone number has no sensible default, and inventing one would be worse than
 * showing none. Separators are placed BETWEEN the parts that survived, never
 * interpolated into fixed slots, so suppression can't leave a stray dot behind.
 *
 * 🔴 <b>There is NO cross-brand fallback in either direction.</b> Each caller passes
 * ONE source — Phoenix's Brand Identity on the native funnel, the partner's own
 * record on a branded one. A blank field means that element is absent; it never
 * means the other party's details appear. A Phoenix address on a white-labelled
 * page would defeat the white label, and it would be a NEW element on partner
 * footers, which carry no email at all today.
 *
 * <b>Order is the CALLER's, not this component's</b>, and the two branches
 * legitimately differ: the native line is email · phone · website (the website is
 * the new element, and it goes after the phone), while the partner line keeps the
 * website · phone it already renders and appends the email — so nothing that a
 * partner funnel shows today moves.
 */
function FooterContactLine({ items }: { items: ContactItem[] }) {
  const rendered = items
    .map(({ kind, value }) => ({ kind, value: value?.trim() }))
    .filter((i): i is { kind: ContactItem["kind"]; value: string } => !!i.value)
    .map(({ kind, value }) => (
      <a
        key={kind}
        className="underline"
        href={
          kind === "email"
            ? `mailto:${value}`
            : kind === "phone"
              ? telHref(value)
              : websiteHref(value)
        }
      >
        {kind === "website" ? websiteLabel(value) : value}
      </a>
    ));

  // An empty <span/> rather than null: the footer row is a two-child flex with
  // `justify-between`, so dropping the child entirely would slide the identity
  // half across the row.
  if (rendered.length === 0) return <span />;
  return (
    <span>
      {rendered.map((el, i) => (
        <span key={el.key}>
          {i > 0 ? " · " : null}
          {el}
        </span>
      ))}
    </span>
  );
}

export function PortalShell({
  children,
  pageTitle,
  brand = "Phoenix Capital",
  wide = false,
  publicFunnel = false,
  partner = null,
  compact = false,
  brandPending = false,
}: PortalShellProps) {
  const auth = usePortalAuth();
  const location = useLocation();
  const onLoginPage = location.pathname === "/portal/login";
  const onPortalRoot = location.pathname === "/portal";
  // Partner-themed chrome only applies on the public funnel. Native (no
  // partner) keeps every class it had before — byte-identical.
  const themed = publicFunnel && !!partner;
  // Scoped to the funnel so no other consumer can be handed a skeleton.
  const pending = publicFunnel && brandPending;
  const headerBg = pending
    ? "bg-gray-mid"
    : themed
      ? "bg-[var(--pq-primary,#0A0A0A)]"
      : "bg-onyx";
  // Unauthenticated visitors get a "Sign in" affordance in the nav — except
  // on the sign-in page itself (redundant) and on the public funnel (an
  // anonymous prospect should see the questions line, not a login).
  const showSignInLink = !publicFunnel && !auth.me && !onLoginPage;
  useDocumentTitle(pageTitle);
  // Shared container width — all four bands (header / banner / main /
  // footer) stay aligned in both the default and the wide layout.
  const container = wide ? "max-w-6xl mx-auto px-space-4 sm:px-space-5" : "max-w-3xl mx-auto px-space-4";
  return (
    <div className={`min-h-screen ${publicFunnel ? "bg-paper" : "bg-white-warm"} text-onyx flex flex-col`}>
      <header className={`${headerBg} text-white-pure shadow-sm`}>
        <div
          className={`${container} ${compact ? "py-space-2" : "py-space-3"} flex flex-wrap items-center justify-between gap-space-3`}
        >
          <div className="flex items-center gap-space-2 min-w-0">
            {pending ? (
              /* The mark's placeholder is sized to the ASSET logo (h-9), which
                 is the taller of the two resolved shapes — so the bar can only
                 ever settle down by a few pixels when a wordmark partner lands,
                 never push the page down. */
              <Skeleton className="h-9 w-[140px] sm:w-[200px] bg-white-pure/25" data-testid="portal-shell-brand-skeleton" />
            ) : themed ? (
              partner.logoMode === "Asset" && partner.logoUrl ? (
                <img
                  src={partner.logoUrl}
                  alt={partner.displayName}
                  className="h-9 w-auto max-w-[140px] sm:max-w-[200px] object-contain"
                />
              ) : (
                <span
                  className="font-bold text-h4 uppercase tracking-[0.2em]"
                  style={{ fontFamily: "var(--pq-font-display)" }}
                >
                  {/* Fall back to the display name when no wordmark text is set
                      (e.g. a wizard-created brand that skipped the logo) —
                      mirrors the PDF letterhead's DisplayName fallback. */}
                  {partner.wordmarkText || partner.displayName}
                </span>
              )
            ) : (
              <span className="font-display font-bold text-h4 tracking-tight">{brand}</span>
            )}
            {/* Phoenix trust-copy, not brand payload — the mark beside it is
                what identifies the site, and this caption reads oddly under
                somebody else's logo. Withheld while pending for the same reason
                the wordmark is: it is a Phoenix cue on a route that may not be
                Phoenix's. */}
            {compact || pending ? null : (
              <span className="text-caption text-gold uppercase tracking-wide">Secure Portal</span>
            )}
          </div>
          {pending ? (
            <Skeleton className="h-5 w-[180px] hidden sm:block bg-white-pure/25" />
          ) : publicFunnel ? (
            <a
              href={themed ? telHref(partner.contactPhone) : "tel:+15722183343"}
              className="text-small text-gold hidden sm:inline whitespace-nowrap"
            >
              ✆ Questions? {themed ? partner.contactPhone : SUPPORT_PHONE}
            </a>
          ) : showSignInLink ? (
            <Link
              to="/portal/login"
              className="text-small underline text-white-pure/80 hidden sm:inline"
            >
              Sign in
            </Link>
          ) : pageTitle ? (
            <span className="text-small text-white-pure/80 hidden sm:inline">{pageTitle}</span>
          ) : null}
        </div>
      </header>

      {/* The trust band. Written to reassure a cold anonymous visitor, so it is
          dropped for an operator who was handed this screen by a colleague —
          and it is the one strip that says "Phoenix Capital" in prose even when
          the rest of the chrome is a partner's. */}
      {compact ? null : (
      <div className={publicFunnel ? "bg-cream border-y border-line text-ink" : "bg-gold/10 border-y border-gold/30 text-onyx"}>
        <div className={`${container} py-space-2 text-caption flex items-center justify-between gap-space-3`}>
          {pending ? (
            /* The band names Phoenix in prose, so it waits with the rest of the
               chrome. Same height, so the page below does not move on paint. */
            <Skeleton className="h-4 w-full max-w-[34rem]" />
          ) : publicFunnel ? (
            <FunnelTrustBand />
          ) : (
            <span className="flex items-center gap-space-2">
              <LockBadge />
              This is the official {brand} secure portal. Documents are encrypted in transit and at rest.
            </span>
          )}
          {auth.me && !publicFunnel ? (
            <button
              type="button"
              onClick={() => void auth.logout()}
              className="underline text-caption shrink-0"
            >
              Sign out
            </button>
          ) : null}
        </div>
      </div>
      )}

      <main className="flex-1">
        <div className={`${container} py-space-4 sm:py-space-5`}>
          {children}
        </div>
      </main>

      <footer className={`border-t ${publicFunnel ? "border-line bg-white-pure" : "border-border-light bg-white-pure"}`}>
        <div className={`${container} py-space-3 text-caption ${publicFunnel ? "text-ink-mid" : "text-gray-mid"} flex flex-wrap items-center justify-between gap-space-2`}>
          {pending ? (
            /* Whose city, website and phone these are is the question being
               resolved, so both halves wait together. */
            <>
              <Skeleton className="h-4 w-[9rem]" />
              <Skeleton className="h-4 w-[14rem]" />
            </>
          ) : publicFunnel ? (
            themed ? (
              <>
                <span>{partner.contactCity}</span>
                {/* The partner's OWN details, or nothing — never Phoenix's. Website
                    and phone keep the order they already render in; the email is
                    appended, so nothing on a live partner footer moves. */}
                <FooterContactLine
                  items={[
                    { kind: "website", value: partner.contactWebsite },
                    { kind: "phone", value: partner.contactPhone },
                    { kind: "email", value: partner.contactEmail },
                  ]}
                />
              </>
            ) : (
              <>
                {/* The company name and the administrator descriptor stay literal
                    prose, deliberately. Brand Identity's COMPANY NAME holds
                    "Phoenix Capital Solutions" (no ", LLC") and its TAGLINE is a
                    different sentence entirely, so wiring these two would silently
                    drop the entity suffix and swap the regulated role descriptor
                    for a marketing line. Only the contact elements are wired. */}
                <span>Phoenix Capital Solutions, LLC · Loan &amp; Note Administrator</span>
                <PhoenixFooterContact />
              </>
            )
          ) : (
            <>
              <span>
                Need help? <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </span>
              {onPortalRoot ? null : (
                <Link to="/portal" className="underline">My dashboard</Link>
              )}
            </>
          )}
        </div>
        {/* White-label: the LOCKED Phoenix attribution — on EVERY funnel view
            (native AND partner). Phoenix-styled with neutral tokens that are
            NOT re-pointed (gray/border/white), so a partner theme can never
            recolor or remove it. */}
        {publicFunnel ? (
          <div className="border-t border-border-light bg-white-pure">
            <div className={`${container} py-space-2 text-caption text-gray-mid text-center`}>
              {POWERED_BY_PHOENIX}
            </div>
          </div>
        ) : null}
      </footer>
    </div>
  );
}

/**
 * Phoenix's own funnel-footer contact line, read from Brand Identity.
 *
 * 🔴 <b>A SEPARATE COMPONENT so the fetch is scoped to the NATIVE funnel</b> — the
 * same reason {@link FunnelTrustBand} is one. A hook runs wherever it is written, so
 * calling this in `PortalShell`'s body would add a request to every pre-auth page
 * sharing this shell (login, redeem, password reset, the broker application,
 * set-password) and to every PARTNER funnel, which must never read these values at
 * all.
 *
 * Until it resolves, and forever if it fails, the line renders empty. That is the
 * honest state: the footer already suppresses any element Brand Identity has not
 * been given, so "no contact line yet" is a state the design covers rather than a
 * hole. It is deliberately NOT backed by hardcoded fallbacks — a stale literal that
 * silently outlives an admin's edit is the defect this change exists to remove.
 */
function PhoenixFooterContact() {
  const [contact, setContact] = useState<PhoenixContact | null>(null);

  useEffect(() => {
    let alive = true;
    // The fetcher swallows every failure and resolves null, so there is no catch
    // arm here to get wrong.
    void fetchPhoenixContact().then((c) => {
      if (alive && c) setContact(c);
    });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <FooterContactLine
      items={[
        { kind: "email", value: contact?.email },
        { kind: "phone", value: contact?.phone },
        // D1 — the new element, and it goes after the phone.
        { kind: "website", value: contact?.website },
      ]}
    />
  );
}

/**
 * The public funnel's trust band — four staff-editable segments and a lock icon.
 *
 * 🔴 <b>A SEPARATE COMPONENT so the copy fetch is scoped to the funnel.</b>
 * `useFunnelCopy()` is a hook, so it runs wherever it is written, and it RESOLVES
 * STANDALONE (issuing its own request) whenever no `PrequalBrandProvider` is
 * mounted above it. Calling it in `PortalShell`'s body would therefore add an API
 * call to every pre-auth page that shares this shell — login, redeem, password
 * reset, the broker application, set-password — none of which render a trust band
 * at all. Rendering it only under `publicFunnel` keeps the hook, and the request,
 * on the one surface that uses the words. Pinned by `prequalBrandResolution.test.tsx`.
 *
 * The lock icon stays code, not copy: it is a glyph, not a sentence, and an admin
 * emptying a text block must never be able to leave a bare icon behind.
 */
function FunnelTrustBand() {
  const { marketing } = useFunnelCopy();
  // A segment published blank drops out with its separator (D4): the dots are
  // placed BETWEEN the segments that remain, so a blank one never leaves a
  // leading, trailing or doubled dot — and the lock goes with its own segment.
  const segments = [
    { key: "secure", text: marketing.trustSecurePortal, lock: true },
    { key: "duration", text: marketing.trustDuration, lock: false },
    { key: "no-credit-pull", text: marketing.trustNoCreditPull, lock: false },
    { key: "free", text: marketing.trustFree, lock: false },
  ].filter((segment) => hasText(segment.text));
  if (segments.length === 0) return null;
  return (
    <span className="flex flex-wrap items-center gap-x-space-2 gap-y-space-1" data-testid="prequal-trust-band">
      {segments.map((segment, i) => (
        <Fragment key={segment.key}>
          {i > 0 && <GoldDot />}
          {segment.lock ? (
            <span className="flex items-center gap-space-1">
              <LockBadge />
              {segment.text}
            </span>
          ) : (
            <span>{segment.text}</span>
          )}
        </Fragment>
      ))}
    </span>
  );
}

function GoldDot() {
  return <span aria-hidden="true" className="text-gold-deep">·</span>;
}

function LockBadge() {
  return (
    <svg
      aria-hidden="true"
      className="w-3 h-3 shrink-0 text-onyx"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      data-testid="portal-shell-lock"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}
